// A recipe: names in three scripts, ingredients checklist, steps, and the private kitchen notes.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError, thumbURL } from '../api';
import { ReelPlayer } from '../components/ReelPlayer';
import { Link, navigate } from '../router';
import { Back, Bag, Check, Folder, Lock, Play, Pot, Share, Trash, VegMark } from '../icons';
import { BottomNav, ErrorBox, GroceryFab, useToast } from '../components/Shell';
import { duration, isInstagramURL, localDay } from '../lib/format';
import { shortDateL, t as tr, useT, uiLang } from '../i18n';
import { AddToAlbumSheet } from '../components/AlbumSheets';
import { useProviders } from '../commerce';
import { useScript } from '../script';
import { load, save, KEYS } from '../storage';
import { inGrocery, toggleGrocery } from '../grocery-selection';
import type { Ingredient, Kitchen, ReelDetail, Script, Verdict } from '../types';

/** Back goes where the reader came from (Home, an album, search…), else Home. */
function goBack() {
  if (history.length > 1) history.back();
  else navigate('/');
}

export function Recipe({ id }: { id: number }) {
  const t = useT();
  const [reel, setReel] = useState<ReelDetail | null>(() => load<ReelDetail | null>(`er.cache.reel.${id}`, null));
  const [err, setErr] = useState('');
  const [script, setScript] = useScript();
  const [toast, showToast] = useToast();

  const refresh = () =>
    api.reel(id).then((r) => { setReel(r); save(`er.cache.reel.${id}`, r); setErr(''); }).catch((e: ApiError) => {
      if (e.status === 404) setErr(tr('recipe.notInLibrary'));
      else if (e.status !== 401) setErr(e.message);
    });
  useEffect(() => { refresh(); }, [id]);
  const reading = reel?.status === 'queued' || reel?.status === 'processing';
  useEffect(() => {
    if (!reading) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [reading]);

  if (err && !reel) {
    return (
      <div class="page">
        <div class="backbar"><button class="icon-btn" aria-label={t('common.back')} onClick={goBack}><Back /></button></div>
        <ErrorBox message={err} onRetry={err === tr('recipe.notInLibrary') ? undefined : refresh} />
      </div>
    );
  }
  if (!reel) return <div class="page"><div class="skeleton" style="height:240px;margin:56px 16px" aria-label={t('common.loading')} /></div>;

  const c = reel.content;
  const share = async () => {
    const url = `${location.origin}/r/${id}`;
    const title = c?.dish_name_en || 'Recipe';
    try {
      if (navigator.share) await navigator.share({ title, text: `${title} — on Eat Recipe`, url });
      else { await navigator.clipboard.writeText(url); showToast(t('recipe.copied')); }
    } catch { /* user cancelled */ }
  };

  return (
    <div class="page with-nav">
      <div class="backbar">
        <button class="icon-btn" aria-label={t('common.back')} onClick={goBack}><Back /></button>
        <button class="icon-btn" aria-label={t('recipe.share')} onClick={share}><Share /></button>
      </div>

      {reading && <ReadingNote />}
      {reel.status === 'failed' && <FailedNote id={id} onDone={refresh} onToast={showToast} />}
      {c && <RecipeBody reel={reel} c={c} script={script} setScript={setScript} onToast={showToast} onChange={refresh} />}
      {!c && !reading && reel.status !== 'failed' && <div class="hint-box" style="margin:16px">{t('recipe.noRecipe')}</div>}

      <RemoveButton id={id} onToast={showToast} />
      {toast}
      <GroceryFab />
      <BottomNav current="" />
    </div>
  );
}

function RecipeBody({ reel, c, script, setScript, onToast, onChange }: {
  reel: ReelDetail; c: NonNullable<ReelDetail['content']>; script: Script; setScript: (s: Script) => void;
  onToast: (m: string) => void; onChange: () => void;
}) {
  const t = useT();
  const id = reel.id;
  const [albums, setAlbums] = useState<number[]>(reel.albums ?? []);
  const [providers] = useProviders();
  const foodApps = providers.filter((p) => p.kind === 'food');
  const [filing, setFiling] = useState(false);
  useEffect(() => setAlbums(reel.albums ?? []), [reel.albums?.join(',')]);
  const [checked, setChecked] = useState<Record<number, boolean>>(() => load(KEYS.inHand(id), {}));
  const [grocery, setGrocery] = useState(() => inGrocery(id));
  const [capOpen, setCapOpen] = useState(false);
  const [trOpen, setTrOpen] = useState(false);
  useEffect(() => save(KEYS.inHand(id), checked), [checked]);

  const ings = c.ingredients_en.map((en, i) => ({
    en, ta: c.ingredients_ta[i] ?? { name: '', quantity: '' }, tg: c.ingredients_translit[i] ?? { name: '', quantity: '' },
  }));
  const pick = (g: { en: Ingredient; ta: Ingredient; tg: Ingredient }): Ingredient =>
    (script === 'ta' ? g.ta : script === 'tg' ? g.tg : g.en).name ? (script === 'ta' ? g.ta : script === 'tg' ? g.tg : g.en) : g.en;
  const steps = script === 'ta' && c.steps_ta.length ? c.steps_ta : c.steps_en;
  const inHand = ings.filter((_, i) => checked[i]).length;
  const vegLabel = c.is_veg === true ? t('common.veg') : c.is_veg === false ? t('common.nonveg') : '';
  const meta = [vegLabel, c.cuisine, reel.duration_sec ? t('recipe.reelLength', { d: duration(reel.duration_sec) }) : '',
    c.spoken_language === 'ta' ? t('recipe.taVoice') : c.spoken_language === 'en' ? t('recipe.enVoice') : ''].filter(Boolean).join(' · ');
  const k = reel.kitchen;
  const handle = reel.creator_handle;

  return (
    <>
      <ReelPlayer id={id} shortcode={isInstagramURL(reel.url) ? reel.shortcode : ''} cover={thumbURL(id)} name={c.dish_name_en || c.dish_name_ta} />
      <section style="padding:6px 20px 0;display:flex;flex-direction:column;gap:4px">
        <div class="card-meta"><VegMark veg={c.is_veg} /><span>{meta}</span></div>
        {c.dish_name_ta && <h1 lang="ta" class="display" style="margin:4px 0 0;font-weight:800;font-size:30px;line-height:1.35;text-wrap:pretty">{c.dish_name_ta}</h1>}
        <div class="display" style="font-weight:600;font-size:19px;line-height:1.25">{c.dish_name_en}</div>
        {c.dish_name_translit && <div class="muted" style="font-size:15px">{c.dish_name_translit}</div>}
        {k && k.cooked_count > 0 && (
          <a href="#my-notes" class="pill" style="align-self:flex-start;margin-top:8px;min-height:32px;padding:0 12px;font-size:13px">
            <Check size={14} weight={2.4} />{t('recipe.cookedLast', { n: k.cooked_count, date: k.cooked_today ? t('recipe.today') : shortDateL(k.last_cooked) })}
          </a>
        )}
      </section>

      <section class="card" style="margin:16px 16px 0;padding:14px;display:flex;flex-direction:column;gap:12px">
        {handle && (
          <div style="display:flex;align-items:center;gap:10px">
            <span aria-hidden="true" style="width:40px;height:40px;border-radius:999px;background:var(--soft);color:var(--accent-text);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px">{handle[0]?.toUpperCase()}</span>
            <div style="display:flex;flex-direction:column;line-height:1.25">
              <span class="small muted">{t('recipe.from')}</span>
              <a href={`https://instagram.com/${handle}`} target="_blank" rel="noopener" style="font-weight:700;font-size:16px">@{handle}</a>
            </div>
          </div>
        )}
        <div style={`display:grid;grid-template-columns:repeat(${isInstagramURL(reel.url) ? 2 : 1},minmax(0,1fr));gap:8px`}>
          {c.steps_en.length > 0 && <Link href={`/r/${id}/cook`} class="btn btn-primary"><Play />{t('recipe.cookMode')}</Link>}
          {isInstagramURL(reel.url) && <a href={reel.url} target="_blank" rel="noopener" class="btn btn-outline" style="gap:6px">{t('common.watchReel')}</a>}
        </div>
        <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px">
          <button class="btn btn-quiet" aria-pressed={grocery} onClick={() => { const on = toggleGrocery(id); setGrocery(on); onToast(on ? t('recipe.groceryAdded') : t('recipe.groceryRemoved')); }}>
            <Bag size={18} />{grocery ? t('recipe.inGrocery') : t('recipe.addGrocery')}
          </button>
          <button class="btn btn-quiet" onClick={() => setFiling(true)}>
            <Folder />{albums.length ? t('recipe.inAlbums', { n: albums.length }) : t('recipe.addAlbum')}
          </button>
        </div>
        {foodApps.map((fa) => fa.coming_soon ? (
          <div class="btn btn-quiet is-soon" style="justify-content:center" aria-disabled="true">
            {t('recipe.orderIt', { app: fa.name })}<span class="soon-badge">{t('soon.badge')}</span>
          </div>
        ) : (
          <Link href={`/order/${fa.id}?dish=${encodeURIComponent(c.dish_name_en || c.dish_name_translit)}${c.is_veg ? '&veg=1' : ''}`}
            class="btn btn-quiet" style="justify-content:center">{t('recipe.orderIt', { app: fa.name })}</Link>
        ))}
        {filing && <AddToAlbumSheet reelId={id} inAlbums={albums} onClose={() => { setFiling(false); onChange(); }} onChange={setAlbums} onToast={onToast} />}
      </section>

      <div class="tabs" role="group" aria-label={t('recipe.script')} style="margin:22px 16px 0">
        {(['ta', 'en', 'tg'] as Script[]).map((s) => (
          <button aria-pressed={script === s} onClick={() => setScript(s)}>{t(`script.${s}`)}</button>
        ))}
      </div>
      {!['ta', 'en'].includes(uiLang()) && <div class="small muted" style="margin:8px 20px 0">{t('recipe.contentLang')}</div>}

      {ings.length > 0 && (
        <section style="padding:22px 16px 0;display:flex;flex-direction:column;gap:8px">
          <div style="display:flex;align-items:baseline;justify-content:space-between;padding:0 4px">
            <h2 class="h2">{t('recipe.ingredients')} · {ings.length}</h2>
            <span class="small muted">{t('recipe.inHand', { n: inHand, total: ings.length })}</span>
          </div>
          <div class="check-list">
            {ings.map((g, i) => {
              const main = pick(g);
              const sub = script === 'ta' ? g.en.name : g.ta.name;
              return (
                <button class={'check-row' + (checked[i] ? ' done' : '')} aria-pressed={!!checked[i]}
                  onClick={() => setChecked({ ...checked, [i]: !checked[i] })}>
                  <span class="checkbox">{checked[i] && <Check weight={3} style="color:var(--on-accent)" />}</span>
                  <span style="flex:1;min-width:0;display:flex;flex-direction:column">
                    <span class="check-name" lang={script === 'ta' ? 'ta' : undefined}>{main.name}</span>
                    {sub && sub !== main.name && <span class="check-sub">{sub}</span>}
                  </span>
                  <span class="check-qty">{main.quantity || g.en.quantity}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section style="padding:26px 16px 0;display:flex;flex-direction:column;gap:12px">
        <h2 class="h2" style="padding:0 4px">{t('recipe.steps')} · {steps.length}</h2>
        {script === 'tg' && steps.length > 0 && <div style="padding:10px 12px;border-radius:12px;background:var(--soft);font-size:14px;line-height:1.5">{t('recipe.tgSteps')}</div>}
        {steps.length === 0 && <div class="note-strip" style="margin:0">{t('recipe.noSteps')}</div>}
        <ol class="steps">
          {steps.map((t, i) => (
            <li class="step"><span class="step-num" aria-hidden="true">{i + 1}</span><span class="step-text" lang={script === 'ta' && c.steps_ta.length ? 'ta' : undefined}>{t}</span></li>
          ))}
        </ol>
      </section>

      {k && <KitchenNotes id={id} k={k} onToast={onToast} onChange={onChange} />}

      <section style="padding:22px 16px 0;display:flex;flex-direction:column;gap:8px">
        {reel.caption && (
          <>
            <button class="accordion" aria-expanded={capOpen} onClick={() => setCapOpen(!capOpen)}>{t('recipe.caption')}<span class="muted" style="font-size:20px">{capOpen ? '−' : '+'}</span></button>
            {capOpen && <div class="muted" style="padding:4px 16px 8px;font-size:15px;line-height:1.6;white-space:pre-wrap;overflow-wrap:anywhere">{reel.caption}</div>}
          </>
        )}
        {(c.transcript_ta || c.transcript_en) && (
          <>
            <button class="accordion" aria-expanded={trOpen} onClick={() => setTrOpen(!trOpen)}>{t('recipe.says')}<span class="muted" style="font-size:20px">{trOpen ? '−' : '+'}</span></button>
            {trOpen && (
              <div class="muted" style="padding:4px 16px 8px;font-size:15px;line-height:1.6;display:flex;flex-direction:column;gap:10px">
                {c.transcript_ta && <p lang="ta" style="margin:0">{c.transcript_ta}</p>}
                {c.transcript_en && <p style="margin:0">{c.transcript_en}</p>}
                {c.transcript_translit && <p style="margin:0;font-style:italic">{c.transcript_translit}</p>}
                <p class="small" style="margin:0">{t('recipe.rough')}</p>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

function KitchenNotes({ id, k, onToast, onChange }: { id: number; k: Kitchen; onToast: (m: string) => void; onChange: () => void }) {
  const t = useT();
  const [kit, setKit] = useState(k);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setKit(k), [k]);

  const cooked = async () => {
    setBusy(true);
    try {
      const next = await api.setCooked(id, !kit.cooked_today);
      setKit({ ...next, notes: kit.notes });
      onChange();
    } catch (e) { onToast((e as ApiError).message); } finally { setBusy(false); }
  };
  const verdict = async (v: Verdict) => {
    const next: Verdict = kit.verdict === v ? '' : v;
    const prev = kit.verdict;
    setKit({ ...kit, verdict: next });
    try { await api.setVerdict(id, next); onChange(); } catch (e) { setKit({ ...kit, verdict: prev }); onToast((e as ApiError).message); }
  };
  const saveNote = async (e: Event) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const n = await api.addNote(id, text);
      setKit({ ...kit, notes: [n, ...kit.notes] });
      setDraft('');
    } catch (ex) { onToast((ex as ApiError).message); } finally { setBusy(false); }
  };
  const delNote = async (noteId: number) => {
    const prev = kit.notes;
    setKit({ ...kit, notes: kit.notes.filter((n) => n.id !== noteId) });
    try { await api.deleteNote(noteId); } catch (e) { setKit({ ...kit, notes: prev }); onToast((e as ApiError).message); }
  };

  const meta = kit.cooked_today
    ? t('notes.logged', { n: kit.cooked_count })
    : kit.cooked_count ? t('notes.cookedBefore', { n: kit.cooked_count, date: shortDateL(kit.last_cooked) }) : t('notes.notYet');

  return (
    <section id="my-notes" style="padding:30px 16px 0;display:flex;flex-direction:column;gap:12px">
      <div style="display:flex;align-items:baseline;justify-content:space-between;padding:0 4px">
        <h2 class="h2">{t('notes.title')}</h2>
        <span class="small muted" style="display:flex;align-items:center;gap:4px"><Lock size={13} weight={2.2} />{t('notes.private')}</span>
      </div>
      <div class="card" style="padding:14px;display:flex;flex-direction:column;gap:12px">
        <button class={'btn ' + (kit.cooked_today ? 'btn-outline' : 'btn-primary')} style="min-height:56px" aria-pressed={kit.cooked_today} disabled={busy} onClick={cooked}>
          <Pot />{kit.cooked_today ? t('notes.cookedToday') : t('notes.cookToday')}
        </button>
        <div class="muted" style="font-size:14px;text-align:center">{meta}</div>
        <div style="height:1px;background:var(--line)" />
        <div style="font-size:15px;font-weight:600">{t('notes.turnOut')}</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px">
          {(['again', 'ok', 'no'] as Verdict[]).map((v) => (
            <button class="verdict" aria-pressed={kit.verdict === v} onClick={() => verdict(v)}>{t(`verdict.${v as 'again' | 'ok' | 'no'}`)}</button>
          ))}
        </div>
      </div>
      <form class="card" style="padding:14px;display:flex;flex-direction:column;gap:10px" onSubmit={saveNote}>
        <label for="er-note" style="font-size:15px;font-weight:600">{t('notes.next')}</label>
        <textarea id="er-note" class="textarea" rows={3} maxLength={2000} value={draft}
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
          placeholder={t('notes.placeholder')} />
        <button type="submit" class="btn btn-dark" style="align-self:flex-end;min-height:44px;border-radius:12px;font-size:15px" disabled={busy || !draft.trim()}>{t('notes.save')}</button>
        {kit.notes.map((n) => (
          <div style="padding:12px;border-radius:12px;background:var(--ground);display:flex;gap:8px;align-items:flex-start">
            <div style="flex:1;display:flex;flex-direction:column;gap:2px">
              <span style="font-size:12px;font-weight:700;color:var(--accent-text);letter-spacing:.04em">{localDay(new Date(n.created_at)) === localDay() ? t('notes.today') : shortDateL(localDay(new Date(n.created_at))).toUpperCase()}</span>
              <span style="font-size:15px;line-height:1.55;white-space:pre-wrap;overflow-wrap:anywhere">{n.text}</span>
            </div>
            <button type="button" class="icon-btn" style="color:var(--muted)" aria-label={t('notes.delete')} onClick={() => delNote(n.id)}><Trash /></button>
          </div>
        ))}
      </form>
    </section>
  );
}

function ReadingNote() {
  const t = useT();
  return (
    <div class="card-processing" style="margin:12px 16px 0" role="status">
      <div class="processing-label"><span class="dot" />{t('card.reading')}</div>
      <div class="display" style="font-weight:700;font-size:19px">{t('recipe.readingTitle')}</div>
    </div>
  );
}

function FailedNote({ id, onDone, onToast }: { id: number; onDone: () => void; onToast: (m: string) => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  return (
    <div class="card" style="margin:12px 16px 0;padding:16px;display:flex;flex-direction:column;gap:10px" role="alert">
      <div class="display" style="font-weight:700;font-size:18px">{t('card.failed')}</div>
      <div class="muted" style="font-size:14px">{t('recipe.failedBody')}</div>
      <button class="btn btn-outline" disabled={busy} onClick={async () => {
        setBusy(true);
        try { await api.retry(id); onDone(); } catch (e) { onToast((e as ApiError).message); } finally { setBusy(false); }
      }}>{busy ? t('common.tryingAgain') : t('common.tryAgain')}</button>
    </div>
  );
}

function RemoveButton({ id, onToast }: { id: number; onToast: (m: string) => void }) {
  const t = useT();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!confirm) return;
    const t = setTimeout(() => setConfirm(false), 5000);
    return () => clearTimeout(t);
  }, [confirm]);
  return (
    <div style="padding:22px 16px 32px;display:flex;flex-direction:column;align-items:center;gap:2px">
      <button class="btn-danger-text" disabled={busy} onClick={async () => {
        if (!confirm) { setConfirm(true); return; }
        setBusy(true);
        try { await api.removeReel(id); navigate('/', { replace: true }); } catch (e) { onToast((e as ApiError).message); setBusy(false); }
      }}>{confirm ? t('recipe.removeConfirm') : t('recipe.remove')}</button>
      <span class="small muted">{confirm ? t('recipe.removeNote') : t('recipe.removeKeep')}</span>
    </div>
  );
}
